/**
 * État de l'application : la partie en cours, l'écran affiché, les préférences
 * d'affichage.
 *
 * Le magasin ne contient **aucune règle de jeu** : il appelle le moteur et range
 * le nouvel état. Tout ce qui décide vit dans `@neopolis/engine`.
 */
import { create } from 'zustand';
import {
  analyzeProblem,
  answerGuidedQuestion,
  classifyProblem,
  createInitialState,
  discoverProblem,
  goToStep,
  isStepUnlocked,
  journaliser,
  isValidIslandCode,
  maxReachableStepIndex,
  deploy,
  evaluateChain,
  placeEquipment,
  setProgramme,
  setTournee,
  recordChapter1Score,
  removeEquipment,
  resolveEvent,
  returnToDesign,
  repondreQuestionnaire,
  reviewClassification,
  setPriorities,
  setWrittenTraceField,
} from '@neopolis/engine';
import type {
  ChainRole,
  ChapterId,
  ClassificationVerdict,
  ConnectedUsefulness,
  GameState,
  JournalEntry,
  JournalEventType,
  Questionnaire,
  ReglageProgramme,
} from '@neopolis/engine';
import { content } from '@neopolis/content';

import { T } from '../textes-interface.js';

import type { SaveAdapter } from '../persistence/save-adapter.js';
import { createLocalStorageAdapter } from '../persistence/local-storage-adapter.js';
import { createClientApi } from '../persistence/client-api.js';
import { createSyncingAdapter } from '../persistence/syncing-adapter.js';
import type {
  AdaptateurSynchronise,
  EtatDeSynchronisation,
} from '../persistence/syncing-adapter.js';

export type Ecran = 'accueil' | 'partie';

export interface AppState {
  ecran: Ecran;
  partie: GameState | null;
  /** Code d'îlot d'une partie enregistrée sur cet appareil, s'il y en a une. */
  ilotReprenable: string | null;
  /** Préférence d'affichage, sans rapport avec la partie. */
  texteAgrandi: boolean;
  erreur: string | null;
  /** État de la sauvegarde : enregistrée, en attente, hors ligne, ou en conflit. */
  synchronisation: EtatDeSynchronisation;
  /**
   * Chapitres que l'enseignant a ouverts. `null` quand on l'ignore — hors
   * ligne, ou sans serveur : dans ce cas rien n'est verrouillé.
   */
  chapitresOuverts: readonly string[] | null;
  /**
   * Résultat de la dernière correction du classement. Volontairement hors de
   * `GameState` : c'est de l'affichage, pas de la partie — il n'a aucune raison
   * d'être sauvegardé ni de revenir à la reprise.
   */
  verdicts: readonly ClassificationVerdict[];
  rapportOuvert: boolean;
  /** Ce que le dernier événement a raconté, et la question à poser à la classe. */
  messagesEvenement: readonly string[];
  debatClasse: string | null;

  initialiser: () => Promise<void>;
  commencer: (islandCode: string) => Promise<boolean>;
  reprendre: () => Promise<boolean>;
  quitter: () => void;
  allerEtape: (stepIndex: number) => void;
  decouvrir: (problemId: string) => void;
  classer: (problemId: string, categoryId: string) => void;
  validerClassement: () => void;
  analyser: (problemId: string, reponse: ConnectedUsefulness) => void;
  definirPriorites: (priorites: readonly string[]) => void;
  ecrireTrace: (fieldId: string, valeur: string) => void;
  /** Efface le message d'erreur affiché, dès que l'élève reprend sa saisie. */
  effacerErreur: () => void;
  ouvrirRapport: () => void;
  fermerRapport: () => void;
  repondreAuQuestionnaire: (reponses: Questionnaire) => void;
  repondreQuestionGuidee: (questionId: string, choiceId: string) => void;
  /** Enregistre le programme d'arrosage réglé par l'îlot, au chapitre 3. */
  reglerProgramme: (reglage: ReglageProgramme) => void;
  /** Enregistre les arrêts du camion pour un jour de sortie, au chapitre 4. */
  organiserTournee: (jour: string, conteneurs: readonly string[]) => void;
  poserEquipement: (slot: ChainRole, equipmentId: string) => void;
  retirerEquipement: (slot: ChainRole, equipmentId: string) => void;
  deployer: () => void;
  resoudreEvenement: (eventId: string, choiceId: string | null) => void;
  revenirAConcevoir: () => void;
  allerChapitre: (chapterId: ChapterId) => void;
  appliquer: (suivant: GameState) => void;
  basculerTexteAgrandi: () => void;
}

/**
 * Adaptateur de persistance : la tablette d'abord, le serveur ensuite.
 * Remplaçable dans les tests.
 */
let adapter: SaveAdapter = createSyncingAdapter(
  createLocalStorageAdapter(),
  createClientApi(import.meta.env['VITE_API_URL'] ?? ''),
);

/** L'adaptateur sait-il se synchroniser ? Un adaptateur de test, non. */
const estSynchronise = (candidat: SaveAdapter): candidat is AdaptateurSynchronise =>
  'surChangement' in candidat;

let desabonner: (() => void) | null = null;

export function setSaveAdapter(remplacant: SaveAdapter): void {
  desabonner?.();
  desabonner = null;
  adapter = remplacant;
  brancherEtatDeSynchronisation();
}

/** Relie l'état de synchronisation de l'adaptateur au magasin. */
function brancherEtatDeSynchronisation(): void {
  if (!estSynchronise(adapter)) {
    return;
  }
  const courant = adapter;
  useGameStore.setState({ synchronisation: courant.etat() });
  desabonner = courant.surChangement((etat) => {
    useGameStore.setState({ synchronisation: etat });
  });
}

/**
 * Ajoute une entrée au journal de séance.
 *
 * L'horodatage vient d'ici : le moteur est pur et ne lit pas l'horloge, ce qui
 * garde ses tests déterministes.
 */
function noterAuJournal(
  state: GameState,
  type: JournalEventType,
  etape: number,
  detail: string | null,
): GameState {
  const entree: JournalEntry = {
    t: Date.now(),
    type,
    chapitre: state.chapterId,
    etape,
    detail,
  };
  return journaliser(state, entree);
}

export const useGameStore = create<AppState>((set, get) => ({
  ecran: 'accueil',
  partie: null,
  ilotReprenable: null,
  texteAgrandi: false,
  erreur: null,
  synchronisation: 'synchronise',
  chapitresOuverts: null,
  verdicts: [],
  rapportOuvert: false,
  messagesEvenement: [],
  debatClasse: null,

  initialiser: async () => {
    const dernier = await adapter.dernierIlot();
    set({ ilotReprenable: dernier });
  },

  commencer: async (islandCode) => {
    // Une nouvelle tentative repart d'un écran propre : le message du refus
    // précédent n'a plus rien à dire.
    set({ erreur: null });

    const code = islandCode.trim().toUpperCase();
    if (!isValidIslandCode(code)) {
      return false;
    }

    // Un code mal recopié ne doit pas lancer une partie fantôme. Quand le
    // serveur répond et ne connaît pas ce code, on refuse tout de suite : sans
    // cela, l'îlot jouerait une heure en local sans que rien ne remonte, et
    // l'enseignant le verrait « pas commencé ».
    //
    // Serveur injoignable, en revanche : on laisse jouer. Le réseau d'un collège
    // tombe, et ce n'est pas une raison d'arrêter la séance.
    if (estSynchronise(adapter)) {
      const connu = await adapter.verifierIlot(code);
      if (connu === 'inconnu') {
        set({ erreur: T.accueil.codeInconnu });
        return false;
      }
    }

    // Saisir son code, c'est retrouver sa partie s'il y en a une : sur une autre
    // tablette, après un onglet fermé, ou le lendemain. Une partie neuve n'est
    // créée que si l'îlot n'a jamais joué.
    const existante = await adapter.charger(code);
    const chapitres = estSynchronise(adapter) ? adapter.chapitresOuverts() : null;

    if (existante) {
      set({
        partie: existante,
        ecran: 'partie',
        ilotReprenable: code,
        erreur: null,
        verdicts: [],
        chapitresOuverts: chapitres,
      });
      return true;
    }

    const partie = noterAuJournal(createInitialState(content, code), 'etape_debut', 0, null);
    await adapter.enregistrer(partie);
    set({
      partie,
      ecran: 'partie',
      ilotReprenable: code,
      erreur: null,
      verdicts: [],
      chapitresOuverts: chapitres,
    });
    return true;
  },

  reprendre: async () => {
    const code = get().ilotReprenable;
    if (code === null) {
      return false;
    }

    const partie = await adapter.charger(code);
    if (partie === null) {
      set({ ilotReprenable: null });
      return false;
    }

    set({
      partie,
      ecran: 'partie',
      erreur: null,
      verdicts: [],
      chapitresOuverts: estSynchronise(adapter) ? adapter.chapitresOuverts() : null,
    });
    return true;
  },

  quitter: () => {
    set({
      ecran: 'accueil',
      partie: null,
      verdicts: [],
      rapportOuvert: false,
      messagesEvenement: [],
      debatClasse: null,
    });
  },

  allerEtape: (stepIndex) => {
    const partie = get().partie;
    if (!partie || !isStepUnlocked(partie, content, stepIndex)) {
      return;
    }
    const result = goToStep(partie, content, stepIndex);
    if (result.ok) {
      // On ferme l'étape quittée et on ouvre la suivante : c'est de ces deux
      // instants que se déduit le temps passé, dans l'analyse de séance.
      const ferme = noterAuJournal(result.state, 'etape_fin', partie.stepIndex, null);
      get().appliquer(noterAuJournal(ferme, 'etape_debut', stepIndex, null));
      // Fin d'étape : le plan demande une sauvegarde à ce moment précis, sans
      // attendre le regroupement des écritures.
      if (estSynchronise(adapter)) {
        void adapter.synchroniser();
      }
    }
  },

  decouvrir: (problemId) => {
    const partie = get().partie;
    if (!partie) return;
    const result = discoverProblem(partie, content, problemId);
    if (result.ok) get().appliquer(result.state);
  },

  repondreAuQuestionnaire: (reponses) => {
    const partie = get().partie;
    if (!partie) return;
    const result = repondreQuestionnaire(partie, content, partie.chapterId, reponses);
    if (result.ok) get().appliquer(result.state);
  },

  classer: (problemId, categoryId) => {
    const partie = get().partie;
    if (!partie) return;
    const result = classifyProblem(partie, content, problemId, categoryId);
    if (result.ok) get().appliquer(result.state);
  },

  validerClassement: () => {
    const partie = get().partie;
    if (!partie) return;
    const { state, verdicts } = reviewClassification(partie, content);
    set({ verdicts });

    // Une erreur de classement par problème mal rangé : c'est ce qui alimente
    // la liste des erreurs fréquentes de l'analyse de séance.
    const journalise = verdicts
      .filter((verdict) => !verdict.correcte)
      .reduce(
        (courant, verdict) =>
          noterAuJournal(courant, 'erreur_classement', partie.stepIndex, verdict.problemId),
        state,
      );

    get().appliquer(journalise);
  },

  analyser: (problemId, reponse) => {
    const partie = get().partie;
    if (!partie) return;
    const result = analyzeProblem(partie, content, problemId, reponse);
    if (result.ok) get().appliquer(result.state);
  },

  definirPriorites: (priorites) => {
    const partie = get().partie;
    if (!partie) return;
    const result = setPriorities(partie, content, priorites);
    if (result.ok) get().appliquer(recordChapter1Score(result.state, content));
  },

  ecrireTrace: (fieldId, valeur) => {
    const partie = get().partie;
    if (!partie) return;
    const result = setWrittenTraceField(partie, content, partie.chapterId, fieldId, valeur);
    if (result.ok) get().appliquer(result.state);
  },

  effacerErreur: () => set({ erreur: null }),

  ouvrirRapport: () => set({ rapportOuvert: true }),
  fermerRapport: () => set({ rapportOuvert: false }),

  reglerProgramme: (reglage) => {
    const partie = get().partie;
    if (!partie) return;
    const result = setProgramme(partie, content, reglage);
    if (result.ok) {
      get().appliquer(result.state);
      set({ erreur: null });
    } else {
      set({ erreur: result.erreur.message });
    }
  },

  organiserTournee: (jour, conteneurs) => {
    const partie = get().partie;
    if (!partie) return;
    const result = setTournee(partie, content, jour, conteneurs);
    if (result.ok) {
      get().appliquer(result.state);
      set({ erreur: null });
    } else {
      set({ erreur: result.erreur.message });
    }
  },

  repondreQuestionGuidee: (questionId, choiceId) => {
    const partie = get().partie;
    if (!partie) return;
    const result = answerGuidedQuestion(partie, content, questionId, choiceId);
    if (result.ok) get().appliquer(result.state);
  },

  poserEquipement: (slot, equipmentId) => {
    const partie = get().partie;
    if (!partie) return;
    const result = placeEquipment(partie, content, slot, equipmentId);
    if (result.ok) {
      get().appliquer(result.state);
      set({ erreur: null });
    } else {
      set({ erreur: result.erreur.message });
    }
  },

  retirerEquipement: (slot, equipmentId) => {
    const partie = get().partie;
    if (!partie) return;
    const result = removeEquipment(partie, content, slot, equipmentId);
    if (result.ok) {
      get().appliquer(result.state);
      set({ erreur: null });
    } else {
      set({ erreur: result.erreur.message });
    }
  },

  deployer: () => {
    const partie = get().partie;
    if (!partie) return;

    // L'essai est noté avant le déploiement : c'est le statut de la chaîne qui
    // intéresse l'analyse, réussi comme raté.
    const evaluation = evaluateChain(partie, content, 'eclairage_nuit');
    const avecEssai = noterAuJournal(partie, 'chaine_essai', partie.stepIndex, evaluation.status);

    const result = deploy(avecEssai, content, 'eclairage_nuit');
    if (result.ok) {
      get().appliquer(result.state);
      set({ erreur: null });
    } else {
      // Le message du budget insuffisant vient du contenu : il est pour l'élève.
      get().appliquer(avecEssai);
      set({ erreur: result.erreur.message });
    }
  },

  resoudreEvenement: (eventId, choiceId) => {
    const partie = get().partie;
    if (!partie) return;
    const result = resolveEvent(partie, content, eventId, choiceId);
    if (!result.ok) return;

    set({
      messagesEvenement: result.resolution.messages,
      debatClasse: result.resolution.debatClasse,
    });
    get().appliquer(result.state);
  },

  revenirAConcevoir: () => {
    const partie = get().partie;
    if (!partie) return;
    const result = returnToDesign(partie, content);
    if (!result.ok) return;

    set({ messagesEvenement: [], debatClasse: null });
    get().appliquer(noterAuJournal(result.state, 'retour_arriere', partie.stepIndex, null));

    // L'étape Concevoir est la deuxième du chapitre 2.
    const apres = get().partie;
    if (apres) get().appliquer({ ...apres, stepIndex: 1 });
  },

  allerChapitre: (chapterId) => {
    const partie = get().partie;
    if (!partie) {
      return;
    }
    set({ rapportOuvert: false, verdicts: [], messagesEvenement: [], debatClasse: null });
    get().appliquer({ ...partie, chapterId, stepIndex: 0 });
  },

  appliquer: (suivant) => {
    set({ partie: suivant });
    void adapter.enregistrer(suivant);
  },

  basculerTexteAgrandi: () => {
    set({ texteAgrandi: !get().texteAgrandi });
  },
}));

brancherEtatDeSynchronisation();

/** Indice de la dernière étape accessible, pour le fil d'Ariane. */
export function etapeMaximale(partie: GameState | null): number {
  return partie ? maxReachableStepIndex(partie, content) : 0;
}
